<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Image storage</h4>
      <el-input
        v-model="search"
        placeholder="Search by name or link"
        clearable
        style="width: 280px"
        @input="onSearch"
      />
    </div>

    <card class="mb-3">
      <div class="d-flex align-items-center flex-wrap">
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png"
          class="d-none"
          @change="onFileSelected"
        />
        <el-button type="primary" :loading="uploading" @click="$refs.fileInput.click()">
          Choose &amp; Upload
        </el-button>
        <span v-if="pending" class="ml-3">
          {{ pending.name }} — {{ pendingDimensions }}
        </span>
        <el-button
          v-if="pending"
          type="success"
          class="ml-3"
          :loading="uploading"
          @click="uploadPending"
        >Upload</el-button>
        <el-button v-if="pending && !uploading" class="ml-2" @click="clearPending">Cancel</el-button>
        <small class="text-muted ml-auto">
          Resized in your browser: up to {{ originalMax }} + {{ cropSuffixes }} crops
        </small>
      </div>

      <div v-if="pendingPreview" class="mt-3">
        <img :src="pendingPreview" class="media-preview" alt="" />
      </div>
    </card>

    <card>
      <el-table :data="items" v-loading="loading" style="width: 100%">
        <el-table-column label="Image" width="150">
          <template slot-scope="{ row }">
            <a :href="row.urls.original" target="_blank" rel="noopener">
              <img :src="withCacheBuster(row.urls.t, row.updated_at)" class="media-thumb" alt="" />
            </a>
          </template>
        </el-table-column>

        <el-table-column label="Link" min-width="420">
          <template slot-scope="{ row }">
            <div class="d-flex align-items-center">
              <el-input :value="row.urls.original" readonly size="mini" />
              <el-button size="mini" class="ml-2" @click="copy(row.urls.original)">Copy</el-button>
            </div>
            <small class="text-muted">
              crops:
              <a
                v-for="(url, suffix) in cropsOf(row)"
                :key="suffix"
                :href="url"
                target="_blank"
                rel="noopener"
                class="mr-2"
              >{{ suffix }}</a>
            </small>
          </template>
        </el-table-column>

        <el-table-column label="Uploaded" width="160">
          <template slot-scope="{ row }">{{ row.created_at }}</template>
        </el-table-column>

        <el-table-column label="" width="170" align="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openCrop(row)">Crop</el-button>
            <el-button size="mini" type="danger" @click="remove(row)">Del</el-button>
          </template>
        </el-table-column>
      </el-table>

      <p v-if="!loading && !items.length" class="text-muted text-center mt-3 mb-0">
        Nothing uploaded yet.
      </p>

      <el-pagination
        v-if="total > perPage"
        class="mt-3"
        layout="prev, pager, next"
        :current-page="page"
        :page-size="perPage"
        :total="total"
        @current-change="goToPage"
      />
    </card>

    <el-dialog title="Crop image" :visible.sync="cropDialogVisible" width="900px" @closed="destroyCropper">
      <div v-if="cropping">
        <div class="media-cropper">
          <img ref="cropImage" :src="croppingSrc" alt="" />
        </div>
        <p class="text-muted mt-2 mb-0">
          The original stays as it is — only the {{ cropSuffixes }} crops are replaced.
        </p>
      </div>
      <span slot="footer">
        <el-button @click="cropDialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="savingCrop" @click="saveCrop">Save crop</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import APIMediaImages from "@/api/modules/media-images";
import {
  CROP_SIZES,
  ORIGINAL_MAX,
  bestFitSize,
  buildVariants,
  buildCrops,
  decodeImage,
} from "./imageVariants";

/** Largest source we let the browser decode — bigger files are a mistake, not a use case. */
const MAX_SOURCE_BYTES = 12 * 1024 * 1024;

export default {
  name: "MediaImagesList",
  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      perPage: 30,
      search: "",
      loading: false,
      uploading: false,
      pending: null,
      pendingPreview: "",
      pendingSize: null,
      cropDialogVisible: false,
      cropping: null,
      croppingSrc: "",
      savingCrop: false,
      cropper: null,
      searchTimer: null,
    };
  },
  computed: {
    cropSuffixes() {
      return Object.keys(CROP_SIZES).join(", ");
    },
    originalMax() {
      return ORIGINAL_MAX.join("×");
    },
    /**
     * Source size, and what it will be stored as when the cap kicks in — so it is obvious
     * before uploading that a 4000x3000 photo is going in as 1024x768.
     */
    pendingDimensions() {
      if (!this.pendingSize) return "";

      const { width, height } = this.pendingSize;
      const [maxWidth, maxHeight] = ORIGINAL_MAX;
      const source = `${width}×${height}`;

      if (width <= maxWidth && height <= maxHeight) return source;

      const stored = bestFitSize(width, height, maxWidth, maxHeight);

      return `${source} → ${stored.width}×${stored.height}`;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await APIMediaImages.list({
          page: this.page,
          per_page: this.perPage,
          search: this.search || undefined,
        });
        this.items = data.items || [];
        this.total = data.total || 0;
      } catch (e) {
        this.notifyError(e, "Could not load the images");
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.load();
      }, 300);
    },
    async onFileSelected(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file) return;

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.$notify({ type: "warning", title: "Only JPG and PNG are allowed" });
        return;
      }
      if (file.size > MAX_SOURCE_BYTES) {
        this.$notify({ type: "warning", title: "The image is larger than 12 MB" });
        return;
      }

      this.pending = file;
      this.pendingPreview = URL.createObjectURL(file);
      try {
        const image = await decodeImage(file);
        this.pendingSize = {
          width: image.width || image.naturalWidth,
          height: image.height || image.naturalHeight,
        };
        if (typeof image.close === "function") image.close();
      } catch (e) {
        this.clearPending();
        this.$notify({ type: "warning", title: "This file is not a readable image" });
      }
    },
    clearPending() {
      if (this.pendingPreview) URL.revokeObjectURL(this.pendingPreview);
      this.pending = null;
      this.pendingPreview = "";
      this.pendingSize = null;
    },
    async uploadPending() {
      if (!this.pending || this.uploading) return;
      this.uploading = true;
      try {
        // Crops are produced here, in the browser; cms-api only stores what it receives.
        const variants = await buildVariants(this.pending);
        await APIMediaImages.upload({ variants });
        this.clearPending();
        this.page = 1;
        await this.load();
        this.$notify({ type: "success", title: "Image uploaded" });
      } catch (e) {
        this.notifyError(e, "Upload failed");
      } finally {
        this.uploading = false;
      }
    },
    openCrop(row) {
      this.cropping = row;
      // Cache-busted and anonymous so the canvas stays untainted and we never crop a stale copy.
      this.croppingSrc = this.withCacheBuster(row.urls.original, Date.now());
      this.cropDialogVisible = true;
      this.$nextTick(() => {
        const image = this.$refs.cropImage;
        if (!image) return;
        image.crossOrigin = "anonymous";
        this.cropper = new Cropper(image, {
          viewMode: 1,
          autoCropArea: 1,
          background: false,
          zoomable: false,
        });
      });
    },
    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      this.cropping = null;
      this.croppingSrc = "";
    },
    async saveCrop() {
      if (!this.cropper || !this.cropping || this.savingCrop) return;
      this.savingCrop = true;
      try {
        const canvas = this.cropper.getCroppedCanvas();
        if (!canvas) throw new Error("Nothing selected to crop");

        const variants = await buildCrops(canvas);
        await APIMediaImages.recrop({ id: this.cropping.id, variants });

        this.cropDialogVisible = false;
        await this.load();
        this.$notify({ type: "success", title: "Crops updated" });
      } catch (e) {
        this.notifyError(e, "Could not save the crop");
      } finally {
        this.savingCrop = false;
      }
    },
    async remove(row) {
      try {
        await this.$confirm(
          "The image and all its crops will be deleted from S3. Any page still using this link will break.",
          "Delete image",
          { type: "warning", confirmButtonText: "Delete", cancelButtonText: "Cancel" }
        );
      } catch (e) {
        return;
      }

      try {
        await APIMediaImages.remove(row.id);
        await this.load();
        this.$notify({ type: "success", title: "Image deleted" });
      } catch (e) {
        this.notifyError(e, "Could not delete the image");
      }
    },
    cropsOf(row) {
      const { original, ...crops } = row.urls;
      return crops;
    },
    withCacheBuster(url, version) {
      const stamp = encodeURIComponent(String(version || ""));
      return stamp ? `${url}?v=${stamp}` : url;
    },
    async copy(url) {
      try {
        await navigator.clipboard.writeText(url);
        this.$notify({ type: "success", title: "Link copied" });
      } catch (e) {
        this.$notify({ type: "warning", title: "Copy failed — select the link and copy it" });
      }
    },
    notifyError(error, fallback) {
      const payload = error && error.response && error.response.data;
      const code = payload && payload.error && payload.error.code;
      this.$notify({ type: "warning", title: code || (error && error.message) || fallback });
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    if (this.pendingPreview) URL.revokeObjectURL(this.pendingPreview);
    this.destroyCropper();
  },
};
</script>

<style scoped>
.media-thumb {
  max-width: 120px;
  max-height: 90px;
  border-radius: 6px;
  object-fit: contain;
}

.media-preview {
  max-width: 320px;
  max-height: 200px;
  border-radius: 6px;
  object-fit: contain;
}

.media-cropper {
  max-height: 60vh;
}

.media-cropper img {
  max-width: 100%;
}
</style>
